import React, { useCallback } from 'react'
import InitialAvatar from 'shared/components/InitialAvatar/InitialAvatar'
import CheckBox from 'shared/ui/CheckBox/CheckBox'
import { ReactComponent as MessageIcon } from 'assets/icons/message-full.svg'
import { ReactComponent as GraduateIcon } from 'assets/icons/student-full.svg'
import { ReactComponent as PeopleIcon } from 'assets/icons/parents.svg'
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg'
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg'
import MessageWithPlaceholders, { setCaretToRawOffset } from './MessageWithPlaceholders'
import s from './SendMessageStep.module.scss'

const SendMessageStep = ({
	cursorOffsetRef,
	messageEditorRef,
	visibleStudents = [],
	hiddenStudentsCount,
	showAllStudents,
	toggleShowAllStudents,
	visibleParents = [],
	hiddenParentsCount,
	showAllParents,
	toggleShowAllParents,
	removeStudent,
	addParents,
	setAddParents,
	messageText,
	setMessageText,
	insertStudentName,
	insertParentName,
}) => {
	const toggleAddParents = () => setAddParents(prev => !prev)
	const focusMessageAfterInsert = useCallback(() => {
		setTimeout(() => {
			const el = messageEditorRef?.current
			if (!el) return
			el.focus()
			const offset = cursorOffsetRef?.current
			if (typeof offset === 'number' && offset >= 0) {
				setCaretToRawOffset(el, offset)
				if (cursorOffsetRef) cursorOffsetRef.current = null
			}
		}, 0)
	}, [messageEditorRef, cursorOffsetRef])

	return (
		<div className={s.root}>
			<div className={s.block}>
				<div className={s.block__head}>
					<div className={s.block__info}>
						<div className={s.block__icon}>
							<GraduateIcon aria-hidden />
						</div>
						<span className={s.block__text}>Selected Students</span>
					</div>
					<label className={s.addParents}>
						<CheckBox isSelected={addParents} onToggle={toggleAddParents} />
						<span className={s.addParents__text}>Add Parents too</span>
					</label>
				</div>

				<div className={s.chipsArea}>
					{visibleStudents.length === 0 ? (
						<span className={s.pillEmpty}>—</span>
					) : (
						<>
							{visibleStudents.map(st => {
								const id = st.id ?? st.studentId ?? st.student_id
								const name = st.name ?? 'Unknown'
								return (
									<span key={id} className={s.pill}>
										<span className={s.pill__avatar}>
											<InitialAvatar
												name={name}
												size={20}
												avatarKey={st.avatarKey}
												avatarSource={st.avatarSource}
												photoUrl={st.photoUrl}
											/>
										</span>
										<span className={s.pill__text}>{name}</span>
										<button
											type="button"
											className={s.pill__remove}
											onClick={() => removeStudent(id)}
											aria-label={`Remove ${name}`}
										>
											<CrossIcon />
										</button>
									</span>
								)
							})}
							{hiddenStudentsCount > 0 && (
								<button
									type="button"
									className={s.pillMore}
									onClick={toggleShowAllStudents}
								>
									{showAllStudents ? 'Hide' : `+${hiddenStudentsCount}`}
								</button>
							)}
						</>
					)}
				</div>
			</div>

			{addParents && (
				<div className={s.block}>
					<div className={s.block__head}>
						<div className={s.block__info}>
							<div className={s.block__icon}>
								<PeopleIcon aria-hidden />
							</div>
							<span className={s.block__text}>Selected Parents</span>
						</div>
					</div>

					<div className={s.chipsArea}>
						{visibleParents.length === 0 ? (
							<span className={s.pillEmpty}>—</span>
						) : (
							<>
								{visibleParents.map(p => (
									<span key={p.id ?? p.name} className={s.pill}>
										<span className={s.pill__text}>{p.name}</span>
									</span>
								))}
								{hiddenParentsCount > 0 && (
									<button
										type="button"
										className={s.pillMore}
										onClick={toggleShowAllParents}
									>
										{showAllParents ? 'Hide' : `+${hiddenParentsCount}`}
									</button>
								)}
							</>
						)}
					</div>
				</div>
			)}

			<div className={s.message}>
				<div className={s.message__head}>
					<div className={s.message__icon}>
						<MessageIcon aria-hidden />
					</div>
					<span className={s.message__text}>Message</span>
				</div>

				<div className={s.message__content}>
					<MessageWithPlaceholders
						value={messageText}
						onChange={setMessageText}
						cursorOffsetRef={cursorOffsetRef}
						editorRef={messageEditorRef}
						className={s.textarea}
						placeholder="Type your message..."
						chipClassName={s.messageChip}
					/>
					<div className={s.placeholderButtons}>
						<button
							type="button"
							className={s.addPlaceholder}
							onClick={() => {
								insertStudentName()
								focusMessageAfterInsert()
							}}
						>
							<span className={s.addPlaceholder__icon}>
								<PlusIcon />
							</span>
							<span className={s.addPlaceholder__text}>Add Student's Name</span>
						</button>
						<button
							type="button"
							className={s.addPlaceholder}
							onClick={() => {
								insertParentName()
								focusMessageAfterInsert()
							}}
						>
							<span className={s.addPlaceholder__icon}>
								<PlusIcon />
							</span>
							<span className={s.addPlaceholder__text}>Add Parent's Name</span>
						</button>
					</div>
				</div>
			</div>
		</div>
	)
}

export default SendMessageStep
